import React, { FC, useState } from 'react';
import { styled } from 'styled-components';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  Icon,
  faChevronDown,
  faChevronUp,
  faCircle,
  faCircleCheck,
  faCircleHalfStroke,
  faFileCircleCheck,
  faFileCircleExclamation,
  faFileCircleQuestion,
  faFileCircleXmark,
  faTrashArrowUp,
  faTrashCan,
  faTriangleExclamation,
} from '@/ui/components';
import { useTranslate } from '@/translations';
import { TranslationKey } from '@/translations/types';

type LegendVariant = 'new' | 'changed' | 'deleted' | 'selected';

type IconLegendItem = {
  key: string;
  icon: IconDefinition;
  color: string;
  labelKey: TranslationKey;
};

const BORDER_ITEMS: { variant: LegendVariant; labelKey: TranslationKey }[] = [
  { variant: 'new', labelKey: 'legendNewDamage' },
  { variant: 'changed', labelKey: 'legendChangedDamage' },
  { variant: 'deleted', labelKey: 'legendDeletedDamage' },
  { variant: 'selected', labelKey: 'legendSelectedDamage' },
];

const PT_ICON_ITEMS: IconLegendItem[] = [
  {
    key: 'not-started',
    icon: faCircle,
    color: '#595959',
    labelKey: 'notStarted',
  },
  {
    key: 'in-progress',
    icon: faCircleHalfStroke,
    color: '#1d4ed8',
    labelKey: 'inProgress',
  },
  {
    key: 'parts-missing',
    icon: faTriangleExclamation,
    color: '#c2410c',
    labelKey: 'partsMissing',
  },
  {
    key: 'complete',
    icon: faCircleCheck,
    color: '#15803d',
    labelKey: 'complete',
  },
  {
    key: 'amended',
    icon: faFileCircleQuestion,
    color: '#d48806',
    labelKey: 'amendedSinceLastReview',
  },
  {
    key: 'approved',
    icon: faFileCircleCheck,
    color: '#12b76a',
    labelKey: 'legendApprovedDamage',
  },
  {
    key: 'rejected',
    icon: faFileCircleXmark,
    color: '#d92d20',
    labelKey: 'legendRejectedDamage',
  },
  {
    key: 'review',
    icon: faFileCircleExclamation,
    color: '#d48806',
    labelKey: 'ptReviewRequired',
  },
];

const ACTION_ICON_ITEMS: IconLegendItem[] = [
  {
    key: 'delete',
    icon: faTrashCan,
    color: '#c92a2a',
    labelKey: 'delete',
  },
  {
    key: 'un-delete',
    icon: faTrashArrowUp,
    color: '#FD7E14',
    labelKey: 'unDelete',
  },
];

const LegendBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 10px;
  padding: 12px 8px;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  background: #fafafa;
`;

const LegendHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const HeaderTitle = styled.h3`
  margin: 0;
  color: #434343;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
`;

const ToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #595959;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
`;

const Section = styled.section`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 24px;

  & + & {
    padding-top: 12px;
    border-top: 1px solid #e8e8e8;
  }
`;

const SectionTitle = styled.h3`
  flex: 0 0 100%;
  margin: 0;
  color: #8c8c8c;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
`;

const LegendItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #434343;
  font-size: 13px;
  line-height: 1.2;
`;

const IconMark = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ $color }) => $color};
  font-size: 18px;
`;

const Swatch = styled.span<{ $variant: LegendVariant }>`
  display: inline-block;
  flex-shrink: 0;
  width: 36px;
  height: 18px;
  box-sizing: border-box;
  background: #fff;
  border: 1px solid #ddd;

  ${({ $variant }) => {
    switch ($variant) {
      case 'new':
        return 'border: 2px solid green;';
      case 'changed':
        return 'border: 2px solid orange;';
      case 'deleted':
        return 'border: 2px solid red;';
      case 'selected':
        return 'border: 2px solid #2f6fed;';
      default:
        return '';
    }
  }}
`;

const IconItems: FC<{ items: IconLegendItem[] }> = ({ items }) => {
  const { t } = useTranslate();

  return (
    <>
      {items.map(({ key, icon, color, labelKey }) => (
        <LegendItem key={key}>
          <IconMark
            $color={color}
            data-testid={`legend-icon-${key}`}
            aria-hidden
          >
            <Icon icon={icon} />
          </IconMark>
          <span>= {t(labelKey)}</span>
        </LegendItem>
      ))}
    </>
  );
};

export const DamageColourLegend: FC = () => {
  const { t } = useTranslate();
  const [expanded, setExpanded] = useState(false);

  return (
    <LegendBox role="region" aria-label={t('damageColourLegend')}>
      <LegendHeader>
        <HeaderTitle>{t('damageColourLegend')}</HeaderTitle>
        <ToggleButton
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((current) => !current)}
        >
          {expanded ? t('collapse') : t('expand')}
          <Icon icon={expanded ? faChevronUp : faChevronDown} />
        </ToggleButton>
      </LegendHeader>
      {expanded ? (
        <>
          <Section aria-label={t('legendBorderColour')}>
            <SectionTitle>{t('legendBorderColour')}</SectionTitle>
            {BORDER_ITEMS.map(({ variant, labelKey }) => (
              <LegendItem key={variant}>
                <Swatch
                  $variant={variant}
                  data-testid={`legend-swatch-${variant}`}
                  aria-hidden
                />
                <span>= {t(labelKey)}</span>
              </LegendItem>
            ))}
          </Section>
          <Section aria-label={t('preTime')}>
            <SectionTitle>{t('preTime')}</SectionTitle>
            <IconItems items={PT_ICON_ITEMS} />
          </Section>
          <Section aria-label={t('actions')}>
            <SectionTitle>{t('actions')}</SectionTitle>
            <IconItems items={ACTION_ICON_ITEMS} />
          </Section>
        </>
      ) : null}
    </LegendBox>
  );
};
